import { Button } from "components/Button";
import Stack from "components/Stack";
import FontAwesomeIcon from "components/FontAwesomeIcon/FontAwesomeIcon";
import downloadFile from "utils/downloadFile";
import getFileExtension from "utils/getFileExtension";

type DocumentPreviewerProps = {
  document: {
    documentURL?: string;
    url?: string;
    documentExtension?: string;
    extension?: string;
  };
  type?: "preview" | "review" | "edit";
  onCancel: () => void;
  onDelete?: () => void;
  onReject?: () => void;
  onAccept?: () => void;
};

const DocumentPreviewer = ({
  document: doc,
  type = "preview",
  onCancel,
  onDelete = () => {},
  onReject = () => {},
  onAccept = () => {},
}: DocumentPreviewerProps) => {
  const url = doc.documentURL || doc.url;
  const extension = (doc.documentExtension || doc.extension || getFileExtension((url || "").split("?")[0] || "") || "").toLowerCase();

  const handleDownload = () => {
    if (!url) return;
    if (extension === "rar") {
      const link = document.createElement("a");
      link.href = url;
      link.download = "";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    downloadFile(url);
  };

  return (
    <Stack className="min-w-[100vw] h-screen space-y-4 px-20 py-10">
      <div className="flex items-center justify-between gap-2 mx-auto sm:mr-0 sm:ml-auto w-[310px] sm:w-fit">
        <button
          type="button"
          onClick={handleDownload}
          className="p-1 text-white hover:opacity-80 transition-opacity"
          aria-label="Download"
        >
          <FontAwesomeIcon name="fa-solid fa-download" className="text-base" />
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="p-1 text-white hover:opacity-80 transition-opacity"
          aria-label="Close"
        >
          <FontAwesomeIcon name="fa-solid fa-times" className="text-base" />
        </button>
      </div>

      <div className="flex mx-auto w-[310px] sm:w-[700px] h-[500px] justify-center">
        {extension === "pdf" ? (
          <embed
            src={url}
            type="application/pdf"
            height={500}
            width={700}
          />
        ) : extension && ["doc", "ppt", "xls"].some((ext) => extension.startsWith(ext)) ? (
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url || "")}`}
            width="100%"
            height="600"
            title="Document preview"
          />
        ) : (
          <img
            src={url}
            alt="document"
            className="w-full h-full object-contain"
          />
        )}
      </div>

      {type === "edit" && (
        <div className="flex justify-center space-x-4">
          <Button
            variant="danger-secondary"
            size="lg"
            onClick={() => {
              onDelete();
              onCancel();
            }}
          >
            Delete
          </Button>
        </div>
      )}

      {type === "review" && (
        <div className="flex justify-center space-x-4">
          <Button variant="danger-secondary" size="lg" onClick={onReject}>
            <span className="w-24">Deny</span>
          </Button>
          <Button variant="primary" size="lg" onClick={onAccept}>
            <span className="w-24">Approve</span>
          </Button>
        </div>
      )}
    </Stack>
  );
};

export function handlePreviewDocument({
  document,
  openModal,
  closeModal,
  type = "preview",
  onDelete = () => {},
  onReject = () => {},
  onAccept = () => {},
}: {
  document: { url?: string; documentURL?: string };
  openModal: (data: { content: React.ReactNode; variant?: "default" | "full-page"; showCloseButton?: boolean }) => void;
  closeModal: () => void;
  type?: "preview" | "review" | "edit";
  onDelete?: () => void;
  onReject?: () => void;
  onAccept?: () => void;
}) {
  const url = document.url || document.documentURL;
  const extension = getFileExtension((url || "").split("?")[0] || "");

  openModal({
    content: (
      <DocumentPreviewer
        document={{
          documentURL: url,
          documentExtension: extension,
        }}
        onCancel={closeModal}
        type={type}
        onDelete={onDelete}
        onReject={onReject}
        onAccept={onAccept}
      />
    ),
    variant: "full-page",
    showCloseButton: false,
  });
}

export default DocumentPreviewer;
